/**
 * Canonical authored-channel delivery-plan compiler.
 *
 * Designer and the server persist this output with the survey version.  The
 * runtime consumes the pinned copy; it never re-runs this compiler against a
 * mutable survey.  Keep this module dependency-free so every compiler caller
 * (Designer and SuperPanel import) gets the same artifact.
 */

const PROFILE_VERSION = 'channel-capabilities-2026-09-01';

const EXECUTION_CHANNELS = Object.freeze([
  'browser', 'whatsapp', 'messenger', 'sms', 'rcs', 'googlechat', 'msteams', 'slack',
]);

const LEGACY_EXECUTION_ALIASES = Object.freeze({ email: 'browser', web: 'browser', teams: 'msteams', google_chat: 'googlechat' });

function canonicalExecutionChannel(value) {
  const token = String(value || '').trim().toLowerCase();
  return LEGACY_EXECUTION_ALIASES[token] || token;
}

function canonicalChannels(values) {
  const seen = new Set();
  const out = [];
  for (const value of Array.isArray(values) ? values : []) {
    const channel = canonicalExecutionChannel(value);
    if (!EXECUTION_CHANNELS.includes(channel) || seen.has(channel)) continue;
    seen.add(channel);
    out.push(channel);
  }
  return out;
}

function questionKind(question = {}) {
  return String(question.question_type || '').trim().toLowerCase();
}

function isNonInputMedia(question = {}) {
  return question.collectsInput !== true
    && question.node_class === 'external_event'
    && question.event_kind === 'media'
    && question.awaits === 'none';
}

function isInputBearing(question = {}) {
  return question.collectsInput === true || question.awaits === 'result' || question.awaits === 'ack';
}

function mediaKind(question = {}) {
  const media = question.media || {};
  const raw = String(media.type || media.kind || media.mime_type || '').toLowerCase();
  if (raw.includes('video')) return 'video';
  if (raw.includes('audio')) return 'audio';
  if (raw.includes('image')) return 'image';
  return 'media';
}

const DISPLAY_SUPPORT = Object.freeze({
  browser: new Set(['media', 'image', 'video', 'audio']),
  whatsapp: new Set(['image', 'audio']),
  messenger: new Set(['image', 'audio']),
  sms: new Set([]),
  rcs: new Set(['image']),
  googlechat: new Set(['image']),
  msteams: new Set(['image']),
  slack: new Set(['image']),
});

function overrideFor(overrides, channel, nodeID) {
  const candidate = overrides?.[channel]?.[nodeID];
  if (!candidate || typeof candidate !== 'object') return null;
  return candidate;
}

function defaultDisposition(channel, question) {
  const nodeID = String(question.question_id || '').trim();
  const kind = questionKind(question);
  if (channel === 'browser') return { node_id: nodeID, disposition: 'as_authored' };

  if (isNonInputMedia(question)) {
    const media = mediaKind(question);
    if (DISPLAY_SUPPORT[channel]?.has(media)) return { node_id: nodeID, disposition: 'as_authored' };
    return {
      node_id: nodeID,
      disposition: 'skipped',
      explanation: `This ${media} cannot be delivered by this channel. The survey moves to the next question.`,
    };
  }

  if (isInputBearing(question)) {
    // Some Designer generations left an empty choices/options array on every
    // question. The authored question type, not the mere presence of that
    // legacy array, decides whether this is a multiple-choice question.
    if (kind === 'choice') {
      return {
        node_id: nodeID,
        disposition: 'adapted',
        explanation: 'The question and every answer choice are sent in the chat. The recipient can reply with a choice, or say or enter anything they want.',
        adaptation: { kind: 'numbered_text_choices' },
      };
    }
    // A scale is a bounded number and is collected as an ordinary typed chat
    // answer. File upload and ranking require interaction support beyond that.
    if (question.event_kind === 'file_upload' || kind === 'file' || kind === 'ranking') {
      return {
        node_id: nodeID,
        disposition: 'author_direction_required',
        explanation: 'This answer-bearing node needs an authored channel adaptation or the channel must be removed.',
      };
    }
  }

  return { node_id: nodeID, disposition: 'as_authored' };
}

function normalizeOverride(nodeID, proposed) {
  const disposition = proposed.disposition;
  if (!['as_authored', 'adapted', 'skipped', 'author_direction_required'].includes(disposition)) return null;
  const item = { node_id: nodeID, disposition };
  if (proposed.explanation) item.explanation = String(proposed.explanation);
  if (disposition === 'adapted' && proposed.adaptation && typeof proposed.adaptation === 'object') {
    item.adaptation = { ...proposed.adaptation };
  }
  return item;
}

function deliveryPlanReviewSignature(channels = {}, selectedChannels = []) {
  const rows = [];
  for (const channel of canonicalChannels(selectedChannels)) {
    for (const item of channels[channel]?.node_dispositions || []) {
      rows.push([
        channel,
        String(item.node_id || ''),
        String(item.disposition || ''),
        String(item.adaptation?.kind || ''),
        String(item.adaptation?.placeholder_text || ''),
        String(item.explanation || ''),
      ]);
    }
  }
  return JSON.stringify(rows);
}

function compileChannelDeliveryPlan({ questions = [], selectedChannels = ['browser'], overrides = {}, reviewedAt = null, reviewedBy = null, reviewSignature = null } = {}) {
  const channels = {};
  for (const channel of canonicalChannels(selectedChannels)) {
    const nodeDispositions = [];
    for (const question of Array.isArray(questions) ? questions : []) {
      const nodeID = String(question?.question_id || '').trim();
      if (!nodeID) continue;
      const override = normalizeOverride(nodeID, overrideFor(overrides, channel, nodeID) || {});
      nodeDispositions.push(override || defaultDisposition(channel, question));
    }
    channels[channel] = {
      selected: true,
      node_dispositions: nodeDispositions,
    };
  }
  const signature = deliveryPlanReviewSignature(channels, selectedChannels);
  const acknowledgementIsCurrent = Boolean(reviewedAt && reviewSignature === signature);
  if (acknowledgementIsCurrent) {
    for (const profile of Object.values(channels)) {
      profile.author_acknowledged_at = reviewedAt;
      if (reviewedBy) profile.author_acknowledged_by = reviewedBy;
    }
  }
  return {
    profile_version: PROFILE_VERSION,
    reviewed_at: acknowledgementIsCurrent ? reviewedAt : null,
    review_signature: signature,
    channels,
  };
}

function unresolvedDeliveryPlanItems(plan = {}) {
  const unresolved = [];
  for (const [channel, profile] of Object.entries(plan.channels || {})) {
    for (const item of profile?.node_dispositions || []) {
      if (item.disposition === 'author_direction_required') unresolved.push({ channel, ...item });
    }
  }
  return unresolved;
}

module.exports = {
  PROFILE_VERSION,
  EXECUTION_CHANNELS,
  LEGACY_EXECUTION_ALIASES,
  canonicalExecutionChannel,
  canonicalChannels,
  deliveryPlanReviewSignature,
  compileChannelDeliveryPlan,
  unresolvedDeliveryPlanItems,
};
